import { createStore } from 'vuex'
import axios from 'axios'
import ArticleService from '@/services/ArticleService';
import StatusService from '@/services/StatusService';
import UploadImageService from '@/services/UploadImageService';
import SubscriptionService from '@/services/SubscriptionService';
import FriendRequestService from '@/services/FriendRequestService';
import { v4 as uuidv4 } from 'uuid';
import { scheduleSessionExpiryLogout, clearSessionExpiryTimer } from '@/utils/authSession';

function initializeSession() {
  const sessionId = localStorage.getItem('sessionId');
  const expirationTime = localStorage.getItem('sessionExpiration');

  if (!sessionId || !expirationTime || Date.now() > expirationTime) {
    const newSessionId = uuidv4();
    const newExpirationTime = Date.now() + (7 * 24 * 60 * 60 * 1000); 
    localStorage.setItem('sessionId', newSessionId);
    localStorage.setItem('sessionExpiration', newExpirationTime);
    return newSessionId;
  }

  return sessionId;
}


const currentToken = localStorage.getItem('token')

const DEFAULT_USER_PREFERENCES = {
  defaultBiasView: 'mixed'
};

function withDefaultPreferences(user = {}) {
  return {
    ...user,
    preferences: {
      ...DEFAULT_USER_PREFERENCES,
      ...(user?.preferences || {})
    }
  };
}

function parseCachedUser() {
  try {
    const cachedUser = JSON.parse(localStorage.getItem('user'));
    return cachedUser && typeof cachedUser === 'object' ? cachedUser : {};
  } catch (error) {
    console.warn('Failed to parse cached user:', error);
    localStorage.removeItem('user');
    return {};
  }
}

function parseMeResponse(data = {}) {
  const isNewShape = data.user && typeof data.user === 'object';
  const { entitlements, preferences, ...flatUser } = data;
  const user = isNewShape ? data.user : flatUser;

  return {
    user: withDefaultPreferences({
      ...user,
      preferences: isNewShape ? preferences : DEFAULT_USER_PREFERENCES
    }),
    entitlements
  };
}

const currentUser = withDefaultPreferences(parseCachedUser());

if(currentToken != null) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
}

const store = createStore ({
  state: {
    timer: null,          
    totalTime: 0.1,        
    articleId: null,
    token: currentToken || '',
    user: currentUser,
    entitlements: {
      tier: 'FREE',
      status: 'NONE',
      isTrialing: false,
      trialEndsAt: null,
      currentPeriodEnd: null
    },
    sessionId: initializeSession(),
    articleContentId: 0,
    curatedContentBasedArticles: [],
    peopleFollowingStatuses: [],
    userLoggedInStatuses: [],
    profileImage: [],
    statusFriendsResponse: [],
    scrollDepth: null,
    // Relationship state
    friends: [],
    friendRequests: [],
    pendingRequests: [],
    peopleFollowing: []
  },
  mutations: {
    setSessionId(state, sessionId) {
      state.sessionId = sessionId
    },
    setContentId(state, articleContentId) {
      state.articleContentId = articleContentId
    },
    SET_AUTH_TOKEN(state, token) {
      
      state.token = token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      scheduleSessionExpiryLogout(token);
    },
    SET_USER(state, user) {
      const incoming = user && typeof user === 'object' ? user : {};
      const authorities = Object.prototype.hasOwnProperty.call(incoming, 'authorities')
        ? incoming.authorities
        : state.user?.authorities;
      state.user = withDefaultPreferences({
        ...state.user,
        ...incoming,
        authorities,
        preferences: {
          ...(state.user?.preferences || {}),
          ...(incoming?.preferences || {})
        }
      });
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    SET_PREFERENCES(state, preferences) {
      state.user = withDefaultPreferences({
        ...state.user,
        preferences: {
          ...(state.user?.preferences || {}),
          ...(preferences || {})
        }
      });
      localStorage.setItem('user', JSON.stringify(state.user));
    },
    LOGOUT(state) {
      clearSessionExpiryTimer();
      localStorage.removeItem('pageRefreshed');
      localStorage.removeItem('sessionId'); 
      localStorage.removeItem('sessionExpiration');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.token = '';
      state.user = {};
      state.entitlements = {
        tier: 'FREE',
        status: 'NONE',
        isTrialing: false,
        trialEndsAt: null,
        currentPeriodEnd: null
      };
      state.curatedContentBasedArticles = [];
      state.peopleFollowingStatuses = [];
      state.userLoggedInStatuses = [];
      state.statusFriendsResponse = [];
      state.friends = [];
      state.friendRequests = [];
      state.pendingRequests = [];
      state.peopleFollowing = [];
      axios.defaults.headers.common = {};
    },
    SET_ARTICLE_ID(state, id){
        state.articleId = id
    },
    START_TIMER(state){
      
    if (state.timer) {
      clearInterval(state.timer);
  }
  state.totalTime = 0; 
  state.timer = setInterval(() => {
      state.totalTime++; 
  }, 1000); 
    },
    SET_CURATED_ARTICLES(state, articles) {
      state.curatedContentBasedArticles = articles;
    },
    SET_FOLLOWING_STATUSES(state, statuses) {
      state.peopleFollowingStatuses = statuses;
    },
    SET_USER_LOGGEDIN_STATUSES(state, statuses) {
      state.userLoggedInStatuses = statuses;
    },
    SET_USER_FRIENDS_STATUSES(state, statuses) {
      state.statusFriendsResponse = statuses;
    },
    SET_USER_PROFILE_PICTURE(state, image) {
      state.profileImage = image;
    },
    SET_ENTITLEMENTS(state, payload) {
      state.entitlements = payload;
    },
    SET_FRIENDS(state, friends) {
      state.friends = friends;
    },
    SET_FRIEND_REQUESTS(state, requests) {
      state.friendRequests = requests;
    },
    SET_PENDING_REQUESTS(state, requests) {
      state.pendingRequests = requests;
    },
    SET_PEOPLE_FOLLOWING(state, following) {
      state.peopleFollowing = following;
    }
  },
  getters: {
    subscriptionTier(state) {
      return String(state.entitlements?.tier || 'FREE').toUpperCase();
    },
    isPaidSniperUser(_state, getters) {
      return ['CORE', 'PRO', 'ELITE'].includes(getters.subscriptionTier);
    },
    hasFullSniperAccess(_state, getters) {
      return ['PRO', 'ELITE'].includes(getters.subscriptionTier);
    },
  },
  actions: {
    async fetchUserData({ commit, state }) {
      if (!state.token) {
        return;
      }
      try {
      
        const statusResponse = await StatusService.getPeopleFollowing();
        
        commit('SET_FOLLOWING_STATUSES', statusResponse.data);

        const statusLoggedInResponse = await StatusService.getStatuses();
        commit('SET_USER_LOGGEDIN_STATUSES', statusLoggedInResponse.data);
        const statusFriendsResponse = await StatusService.listfriendsStatus();
        commit('SET_USER_FRIENDS_STATUSES', statusFriendsResponse.data);
        const profilePic = await UploadImageService.getProfileImage();
        commit('SET_USER_PROFILE_PICTURE', profilePic.data);
      } catch (error) {
        if (error !== 'User not authenticated') {
          console.error("Error fetching data:", error);
        }
      }
    },
      async fetchCuratedArticles({ commit }) {
        try {
        const articleResponse = await ArticleService.listFwlsArticlesFromDb();
        commit('SET_CURATED_ARTICLES', articleResponse.data);
      } catch (error) {
        if (error !== 'User not authenticated') {
          console.error("Error fetching data:", error);
        }
      }
    },
    async fetchEntitlements({ commit }) {
      try {
        const res = await SubscriptionService.fetchMe();
        
        const { user, entitlements } = parseMeResponse(res.data);
        
        
        // Update user state (includes id, username, email)
        commit('SET_USER', user);
        
        // Update entitlements state
        commit('SET_ENTITLEMENTS', entitlements);
        console.log('[fetchEntitlements] OK', entitlements);
        
        return res.data;
      } catch (e) {
        if (e?.response?.status === 401) {
          commit('LOGOUT');
          const { default: router } = await import('@/router');
          if (router.currentRoute?.value?.path !== '/login') {
            router.push('/login');
          }
          return;
        }
        console.error('Failed to fetch entitlements:', e);
        // Non-auth failures: stay on current tier default rather than crashing the app
      }
    },
    async refreshRelationships({ commit }) {
      try {
        const [friends, friendRequests, pendingRequests, following] = await Promise.all([
          FriendRequestService.listFriends(),
          FriendRequestService.listFriendRequsts(),
          FriendRequestService.listPendingFriendRequests(),
          FriendRequestService.getPeopleFollowing()
        ]);
        
        commit('SET_FRIENDS', friends.data);
        commit('SET_FRIEND_REQUESTS', friendRequests.data);
        commit('SET_PENDING_REQUESTS', pendingRequests.data);
        commit('SET_PEOPLE_FOLLOWING', following.data);
        
        console.log('[refreshRelationships] Updated relationship state');
      } catch (error) {
        console.error('Failed to refresh relationships:', error);
      }
    }
    }
})

export default store